import { Button } from '@/shared/components';
import { I } from '@/shared/icons';
import { PdfPreview } from './PdfPreview';

export type PreviewKind = 'pdf' | 'image' | 'other';

export function detectPreviewKind(contentType: string): PreviewKind {
  if (contentType === 'application/pdf') return 'pdf';
  if (contentType.startsWith('image/')) return 'image';
  return 'other';
}

/** Der eigentliche Betrachter: PDF-Seite, Bild oder ehrlicher Download-Hinweis. */
export function FilePreviewBody({
  kind,
  url,
  fileName,
  page,
  onPageCount,
  onDownload,
}: {
  kind: PreviewKind;
  url: string;
  fileName: string;
  page: number;
  onPageCount: (n: number) => void;
  onDownload: () => void;
}) {
  if (kind === 'pdf') {
    return <PdfPreview url={url} page={page} onPageCount={onPageCount} />;
  }
  if (kind === 'image') {
    return (
      <div className="flex items-center justify-center">
        <img src={url} alt={fileName} className="max-h-[60dvh] max-w-full object-contain" />
      </div>
    );
  }
  return (
    <div className="text-ink-soft flex flex-col items-center justify-center gap-3 py-12 text-sm">
      <span className="border-line text-ink-faint rounded border px-3 py-1 font-mono text-[10px] uppercase tracking-wider">
        {fileName.split('.').pop()?.toUpperCase() ?? 'FILE'}
      </span>
      <p>Vorschau für diesen Dateityp nicht verfügbar.</p>
      <Button variant="brand" leftIcon={<I name="Download" size={14} />} onClick={onDownload}>
        Herunterladen
      </Button>
    </div>
  );
}

/** Seitenblättern im PDF. An den Grenzen deaktiviert statt still wirkungslos. */
export function PreviewPageNav({
  page,
  pageCount,
  onPrev,
  onNext,
}: {
  page: number;
  pageCount: number;
  onPrev: () => void;
  onNext: () => void;
}) {
  return (
    <div className="text-ink-soft flex items-center text-xs tabular-nums">
      <button
        type="button"
        onClick={onPrev}
        disabled={page <= 1}
        className="hover:text-ink flex h-11 w-11 items-center justify-center disabled:opacity-40"
        aria-label="Vorherige Seite"
      >
        <I name="ArrowLeft" size={14} />
      </button>
      <span className="whitespace-nowrap px-1">
        Seite {page} / {pageCount}
      </span>
      <button
        type="button"
        onClick={onNext}
        disabled={page >= pageCount}
        className="hover:text-ink flex h-11 w-11 items-center justify-center disabled:opacity-40"
        aria-label="Nächste Seite"
      >
        <I name="Arrow" size={14} />
      </button>
    </div>
  );
}

export function PreviewMessage({ label, tone }: { label: string; tone?: 'error' }) {
  return (
    <div
      className={`flex items-center justify-center py-12 text-sm ${
        tone === 'error' ? 'text-sealwax' : 'text-ink-faint'
      }`}
    >
      {label}
    </div>
  );
}
